import "./styles.css";
import "../node_modules/tabulator-tables/dist/css/tabulator_bootstrap5.min.css";
import { scriptMap } from "./scriptMap.ts";

import {
	CellComponent,
	EditModule,
	Filter,
	FilterModule,
	FormatModule,
	InteractionModule,
	PopupModule,
	ResizeColumnsModule,
	ResizeTableModule,
	ResponsiveLayoutModule,
	Sorter,
	SortModule,
	Tabulator,
	TooltipModule,
} from "tabulator-tables";

type SearchEntry = {
	code: string;
	example: string;
	name: string;
	entity?: string;
	age: string;
	block: string;
	category: string;
	script: string;
	tags?: string[];
	notes?: string[];
};

type SearchData = {
	success: boolean;
	data: SearchEntry[];
};

const dataUrl = "/ucd.json";

const categoryMap: { [key: string]: string } = {
	"Cc": "Other, Control",
	"Cf": "Other, Format",
	"Cn": "Other, Not Assigned (no characters in the file have this property)",
	"Co": "Other, Private Use",
	"Cs": "Other, Surrogate",
	"LC": "Letter, Cased",
	"Ll": "Letter, Lowercase",
	"Lm": "Letter, Modifier",
	"Lo": "Letter, Other",
	"Lt": "Letter, Titlecase",
	"Lu": "Letter, Uppercase",
	"Mc": "Mark, Spacing Combining",
	"Me": "Mark, Enclosing",
	"Mn": "Mark, Nonspacing",
	"Nd": "Number, Decimal Digit",
	"Nl": "Number, Letter",
	"No": "Number, Other",
	"Pc": "Punctuation, Connector",
	"Pd": "Punctuation, Dash",
	"Pe": "Punctuation, Close",
	"Pf": "Punctuation, Final quote (may behave like Ps or Pe depending on usage)",
	"Pi": "Punctuation, Initial quote (may behave like Ps or Pe depending on usage)",
	"Po": "Punctuation, Other",
	"Ps": "Punctuation, Open",
	"Sc": "Symbol, Currency",
	"Sk": "Symbol, Modifier",
	"Sm": "Symbol, Math",
	"So": "Symbol, Other",
	"Zl": "Separator, Line",
	"Zp": "Separator, Paragraph",
	"Zs": "Separator, Space"
}

const exampleMap: { [key: string]: string } = {};

function initExampleMap(data: SearchEntry[]) {
	for (var loop = 0; loop <= 0x1F; loop++) {
		exampleMap[String.fromCodePoint(loop)] = String.fromCodePoint(0x2400 + loop);
	}
	exampleMap["<"] = "&lt;";
	exampleMap[">"] = "&gt;";
	exampleMap["&"] = "&amp;";
	exampleMap["\""] = "&quot;";
}

const nameFilterMap: { [key: string]: string } = {};

function initNameFilterMap(data: SearchEntry[]) {
	for (var letter = 'A'.charCodeAt(0); letter <= 'Z'.charCodeAt(0); letter++) {
		const char = String.fromCharCode(letter);
		nameFilterMap[char] = `LETTER ${char}`;
		nameFilterMap[char.toLowerCase()] = `LETTER ${char}`;
	}
	nameFilterMap['0'] = `DIGIT ZERO`;
	nameFilterMap['1'] = `DIGIT ONE`;
	nameFilterMap['2'] = `DIGIT TWO`;
	nameFilterMap['3'] = `DIGIT THREE`;
	nameFilterMap['4'] = `DIGIT FOUR`;
	nameFilterMap['5'] = `DIGIT FIVE`;
	nameFilterMap['6'] = `DIGIT SIX`;
	nameFilterMap['7'] = `DIGIT SEVEN`;
	nameFilterMap['8'] = `DIGIT EIGHT`;
	nameFilterMap['9'] = `DIGIT NINE`;
	nameFilterMap[' '] = `SPACE`;
	nameFilterMap['='] = `EQUALS`;
	nameFilterMap['~'] = `TILDE`;
	nameFilterMap['-'] = 'MINUS';
	nameFilterMap[':'] = 'COLON';
	nameFilterMap['_'] = 'UNDERSCORE';
	nameFilterMap['?'] = 'QUESTION MARK';
	nameFilterMap['.'] = 'FULL STOP';
}

var useImages = false;

// translates from a hex codepoint string to the actual character
function codeToString(code: string):string {
	return String.fromCodePoint(parseInt(code, 16));
}

function filterCategory(
	headerValue: string,
	rowValue: string,
	rowData: any,
	filterParams: any
) {
	if (!headerValue) return true;

	const headerUpper = headerValue.toUpperCase();
	if (headerValue.length == 2) {
		return rowValue.toUpperCase() == headerUpper;
	}

	const description = categoryMap[rowValue].toUpperCase();

	return description.startsWith(headerUpper);
}

function filterName(
	headerValue: string,
	sortValue: string,
	rowData: any,
	filterParams: any
) {
	if (!headerValue) return true;

	const rowValues = [ rowData.name ];
	if (rowData.notes) {
		rowValues.push(...rowData.notes);
	}
	if (rowData.entity) {
		rowValues.push(rowData.entity.replaceAll(/[^a-z]/gi, ''));
		console.log(`DEBUG: entity rowValues: ${JSON.stringify(rowValues)}`);
	}

	if (headerValue.length == 1) {
		if (headerValue == "^") {
			return true;
		}
		headerValue = nameFilterMap[headerValue] || headerValue;
	}

	if (headerValue.startsWith("^")) {
		// starts with
		if (headerValue.length == 1) {
			return true;
		}
		const search = headerValue.substring(1).toLowerCase();
		for (const rowValue of rowValues) {
			if (rowValue.toLowerCase().startsWith(search)) {
				return true;
			}
		}
		return false;
	}

	if (headerValue.startsWith("/") && headerValue.endsWith("/")) {
		// regex
		const pattern = headerValue.substring(1, headerValue.length - 1);
		try {
			const re = new RegExp(pattern, "i");
			for (const rowValue of rowValues) {
				if (re.test(rowValue)) {
					return true;
				}
			}
			return false;
		} catch (e) {
			// bad regex
			return false;
		}
	}

	// contains
	const search = headerValue.toLowerCase();
	for (const rowValue of rowValues) {
		if (rowValue.toLowerCase().includes(search)) {
			return true;
		}
	}
	return false;
}

function filterTags(
	headerValue: string,
	rowValue: string[],
	rowData: any,
	filterParams: any
) {
	if (!headerValue || headerValue.length == 0) return true;

	const headerVals = headerValue.split(/[ ,]+/);
	const rowVals:string[] = rowValue || [];

	for (const filterVal of headerVals) {
		if (filterVal.startsWith("!")) {
			if (rowVals.indexOf(filterVal.slice(1)) != -1) {
				return false;
			}
		} else {
			if (rowVals.indexOf(filterVal) == -1) {
				return false;
			}
		}
	}
	return true;
}

function fmtCategory(cell: CellComponent) {
	const val = cell.getValue() as string;
	if (!val) {
		return "(missing)";
	}
	return categoryMap[val] || val;
}

function fmtCodepoint(cell: CellComponent) {
	const val = cell.getValue() as string;
	if (!val) {
		return "";
	}
	return `U+${val.toUpperCase()}`;
}
function fmtExample(cell: CellComponent) {
	const val = cell.getValue() as string;
	if (!val) {
		return "";
	}
	if (useImages) {
		const codepoint = cell.getRow().getData().code;
		const prefix = codepoint.slice(0, -2);
		return `<img src="https://ucd-images.fileformat.info/${prefix}/u${codepoint.toLowerCase()}.svg" alt="U+${codepoint}" style="height:2em;">`;
	}

	const mapped = exampleMap[val] || val;
	return `<span style="font-size:2em;">${mapped}</span>`;
}

function fmtTags(cell: CellComponent) {
	const tags = cell.getValue() as string[];
	const notes = cell.getRow().getData().notes as string[] | undefined;
	if (!tags && !notes) {
		return "";
	}

	const container = document.createElement("div");

	if (notes) {
		container.textContent = notes.join(", ") + " ";
	}

	if (tags) {
		const keys = tags.sort();

		for (const key of keys) {
			var el = document.createElement("span");
			el.className =
				"badge border border-primary text-primary me-1 mb-1 text-decoration-none";
			el.textContent = key.replace(/_/g, " ");
			el.style.cursor = "pointer";
			el.onclick = (e) => {
				e.preventDefault();
				e.stopPropagation();
				toggleTagFilter(cell, key);
			}
			container.appendChild(el);
		}
	}

	return container;
}

function imgTooltipFn(imgType: string) {
	return function (e: MouseEvent, cell: CellComponent, onRendered: any) {
		var value = cell.getValue();
		if (!value) {
			return "n/a";
		}

		const handle = cell.getRow().getData().logohandle;

		const el = document.createElement("img");
		el.src = `https://www.vectorlogo.zone/logos/${handle}/${handle}-${imgType}.svg`;
		el.style.height = "256px";

		return el;
	};
}

function imgClickFn(imgType: string, extraParams: string) {
	return function (e: UIEvent, cell: CellComponent) {
		var value = cell.getValue();
		if (!value) {
			return;
		}

		const handle = cell.getRow().getCell("logohandle").getValue();

		const url = `https://svg-viewer.fileformat.info/view.html?url=https://www.vectorlogo.zone/logos/${handle}/${handle}-${imgType}.svg&backUrl=https://www.vectorlogo.zone/logos/${handle}/${extraParams}`;
		window.open(url, "_blank")?.focus();
	};
}

function showError(msg: string) {
	console.log(`ERROR: ${msg}`);
	document.getElementById("loading")!.classList.add("d-none");
	document.getElementById("errdiv")!.classList.remove("d-none");
	document.getElementById("errmsg")!.innerHTML = msg;
}


const tickElement = `<svg enable-background="new 0 0 24 24" height="14" width="14" viewBox="0 0 24 24" xml:space="preserve"><path fill="#2DC214" clip-rule="evenodd" d="M21.652,3.211c-0.293-0.295-0.77-0.295-1.061,0L9.41,14.34  c-0.293,0.297-0.771,0.297-1.062,0L3.449,9.351C3.304,9.203,3.114,9.13,2.923,9.129C2.73,9.128,2.534,9.201,2.387,9.351  l-2.165,1.946C0.078,11.445,0,11.63,0,11.823c0,0.194,0.078,0.397,0.223,0.544l4.94,5.184c0.292,0.296,0.771,0.776,1.062,1.07  l2.124,2.141c0.292,0.293,0.769,0.293,1.062,0l14.366-14.34c0.293-0.294,0.293-0.777,0-1.071L21.652,3.211z" fill-rule="evenodd"></path></svg>`;

function tickLinkFormatter(cell: CellComponent) {
	const value = cell.getValue() as string;
	if (!value) {
		return "";
	}
	var el = document.createElement("a");
	el.href = value;
	el.target = "_blank";
	el.innerHTML = tickElement;
	return el;
}

function tickLinkFilter(
	headerValue: boolean,
	rowValue: string,
	rowData: any,
	filterParams: any
) {
	if (headerValue === true) {
		return rowValue && rowValue.length > 0;
	} else if (headerValue === false) {
		return !rowValue || rowValue.length === 0;
	}
	return true; // null case
}

function toggleColumns(tbl: Tabulator, columns: string[]): void {
	for (const col of columns) {
		const column = tbl.getColumn(col);
		if (column) {
			if (column.isVisible()) {
				column.hide();
			} else {
				column.show();
			}
		}
	}
}

function toggleTagFilter(cell: CellComponent, tag: string): void {
	const tbl = cell.getTable();
	var headerFilter = "";
	const headerFilters = tbl.getHeaderFilters();
	var existingFilter: Filter | null = null;
	for (const hf of headerFilters) {
		if (hf.field == "tags") {
			headerFilter = hf.value;
			existingFilter = hf;
			break;
		}
	}

	if (existingFilter == null) {
		console.log(`adding to blank`);
		tbl.setHeaderFilterValue(cell.getColumn(), tag);
	} else {
		tbl.setHeaderFilterValue(
			cell.getColumn(),
			(existingFilter.value = toggleTagArray(
				headerFilter.split(/[ ,]+/),
				tag
			).join(" "))
		);
	}
	tbl.refreshFilter();
}

function toggleTagArray(tags: string[], tag: string): string[] {
	var idx = tags.indexOf(tag);
	if (idx != -1) {
		tags.splice(idx, 1);
		//tags.push(`!${tag}`);
		return tags;
	}

	idx = tags.indexOf(`!${tag}`);
	if (idx != -1) {
		tags.splice(idx);
		return tags;
	}

	tags.push(tag);
	return tags;
}

// from https://evanhahn.com/getting-the-utf16-bytes-of-javascript-strings/
function* utf16bytes(str:string): Iterable<number> {
	for (let i = 0; i < str.length; i++) {
		const charCode = str.charCodeAt(i);

		// Get the most significant byte.
		// For example, given 0x1234, yield 0x12.
		yield(charCode & 0xff00) >> 8;

		// Get the least significant byte.
		// For example, given 0x1234, yield 0x34.
		yield charCode & 0x00ff;
	}
}

function utf8bytes(str:string): Uint8Array {
	return new TextEncoder().encode(str);
}

async function main() {
	let data: SearchEntry[];
	var detail = false;

	var rawData:any;
	try {
		const resp = await fetch(dataUrl, {
			method: "GET",
			redirect: "follow",
		});
		if (!resp.ok) {
			showError(
				`HTTP Error fetching logo data: ${resp.status} ${resp.statusText}`
			);
			return;
		}
		rawData = (await resp.json() as SearchData);
	} catch (error) {
		showError(`Error fetching Unicode character data: ${error}`);
		return;
	}

	data = rawData.data;

	for (const row of data) {
		row.example = codeToString(row.code);
		row.script = scriptMap[row.script] || row.script;
	}

	initExampleMap(data);
	initNameFilterMap(data);

	console.log(data[0]);

	const qs = new URLSearchParams(window.location.search);
	const sort: Sorter[] = [ { column: "code", dir: "asc" } ];
	const filters: Filter[] = [];
	if (qs) {
		;
		for (const [key, value] of qs.entries()) {
			if (key == "sort") {
				sort[0].column = value;
				continue;
			}
			if (key == "dir") {
				sort[0].dir = (value == "desc") ? "desc" : "asc";
				continue;
			}
			if (key == "detail") {
				detail = (value == "1" || value.toLowerCase() == "true");
				continue;
			}
			if (key == "images") {
				useImages = (value == "1" || value.toLowerCase() == "true");
			}
			if (key && value) {
				filters.push({ field: key, type: "=", value: value });
			}
		}
	}

	Tabulator.registerModule([
		EditModule,
		FilterModule,
		FormatModule,
		InteractionModule,
		PopupModule,
		ResizeColumnsModule,
		ResizeTableModule,
		ResponsiveLayoutModule,
		SortModule,
		TooltipModule,
	]);

	const table = new Tabulator("#unicodetable", {
		autoResize: true,
		data,
		columns: [
			{
				cellClick: (e, cell) => {
					const data = cell.getRow().getData();
					e.preventDefault();
					e.stopPropagation();
					table.alert(
						`${data.name} (U+${data.code}) copied to clipboard`
					);
					setTimeout(() => table.clearAlert(), 1000);
					navigator.clipboard.writeText(data.example);
				},
				field: "",
				formatter: () =>
					`<img src="/images/icons/clipboard.svg" alt="Copy to clipboard" height="16">`,
				headerSort: false,
				hozAlign: "center",
				title: "",
				width: 40,
			},
			{
				cssClass: "p-0 flex justify-content-center align-items-center",
				field: "example",
				formatter: fmtExample,
				headerFilter: "input",
				headerFilterFunc: (
					headerValue,
					rowValue,
					rowData,
					filterParams
				) => {
					if (!headerValue) return true;
					return headerValue == rowValue;
				},
				headerHozAlign: "center",
				headerSort: false,
				hozAlign: "center",
				responsive: 0,
				title: "Example",
				width: 100,
			},
			{
				field: "code",
				formatter: fmtCodepoint,
				headerFilter: "input",
				headerHozAlign: "center",
				hozAlign: "center",
				responsive: 10,
				sorter: function (a, b, aRow, bRow, column, dir, sorterParams) {
					const aInt = parseInt(a, 16);
					const bInt = parseInt(b, 16);
					return aInt - bInt;
				},
				title: "Codepoint",
				width: 130,
			},
			{
				field: "entity",
				headerFilter: "input",
				headerHozAlign: "center",
				hozAlign: "left",
				title: '<a href="https://www.fileformat.info/format/w3c/htmlentity.htm">HTML\u00A0Entity</a>',
				visible: detail,
				width: 175,
			},
			{
				field: "block",
				headerFilter: "input",
				headerHozAlign: "center",
				hozAlign: "center",
				responsive: 20,
				title: '<a href="https://www.fileformat.info/info/unicode/block/index.htm">Block</a>',
				visible: detail,
				width: 175,
			},
			{
				field: "category",
				formatter: fmtCategory,
				headerFilter: "input",
				headerFilterFunc: filterCategory,
				headerHozAlign: "center",
				hozAlign: "center",
				responsive: 40,
				title: '<a href="https://www.fileformat.info/info/unicode/category/index.htm">Category</a>',
				visible: detail,
				width: 200,
			},
			{
				field: "script",
				headerFilter: "input",
				headerFilterFunc: "starts",
				headerHozAlign: "center",
				hozAlign: "center",
				responsive: 50,
				title: "Script",
				visible: detail,
				width: 100,
			},
			{
				field: "age",
				headerFilter: "input",
				headerFilterFunc: "starts",
				headerHozAlign: "center",
				hozAlign: "center",
				responsive: 30,
				title: '<a href="https://www.fileformat.info/info/unicode/version/index.htm">Version</a>',
				visible: detail,
				width: 110,
			},
			{
				field: "utf8",
				formatter: (cell) => {
					const val = cell.getRow().getData().example as string;
					if (!val) {
						return "";
					}
					const bytes = utf8bytes(val);
					const parts: string[] = [];
					for (const b of bytes) {
						parts.push(
							b.toString(16).toUpperCase().padStart(2, "0")
						);
					}
					return parts.join(" ");
				},
				headerHozAlign: "center",
				headerSort: false,
				hozAlign: "center",
				title: "UTF-8",
				visible: detail,
			},
			{
				field: "utf16",
				formatter: (cell) => {
					const val = cell.getRow().getData().example as string;
					if (!val) {
						return "";
					}
					const parts: string[] = [];
					for (const b of utf16bytes(val)) {
						parts.push(
							b.toString(16).toUpperCase().padStart(2, "0")
						);
					}
					return parts.join(" ");
				},
				headerHozAlign: "center",
				headerSort: false,
				hozAlign: "center",
				title: "UTF-16",
				visible: detail,
			},
			{
				title: "Name",
				field: "name",
				formatter: "link",
				formatterParams: {
					labelField: "name",
					url: (cell) => {
						var codepoint = cell.getData().code;
						return `https://www.fileformat.info/info/unicode/char/${codepoint}/index.htm`;
					},
					target: "_blank",
				},
				headerFilter: "input",
				headerFilterFunc: filterName,
				headerPopup: `Use <code>^</code> to search at the beginning<br/>
				Use <code>/regex/</code> to search with a regular expression<br/>
				Also searches Notes`,
				headerPopupIcon:
					'<span class="badge rounded-pill text-bg-primary">?</span>',
				width: 325,
			},
			{
				title: "Notes & Tags",
				field: "tags",
				formatter: fmtTags,
				headerFilter: "input",
				headerFilterFunc: filterTags,
				headerPopup: `Separate multiple tags with space or comma.<br/>
				Prefix a tag with <code>!</code> to exclude it.<br/>
				Notes are searched under Name.`,
				headerPopupIcon:
					'<span class="badge rounded-pill text-bg-primary">?</span>',
				headerSort: false,
				minWidth: 375,
			},
		],
		height: "100%",
		initialHeaderFilter: filters,
		initialSort: [{ column: "code", dir: "asc" }],
		layout: "fitDataStretch",
		placeholder: "No matches",
		//responsiveLayout: "hide",
		footerElement: `<span class="w-100 mx-2 my-1">
				<img src="/favicon.svg" class="pe-2 mb-1" style="height:1.2em;" alt="UnicodeSearch logo"/><span style="font-size:1.0em;font-family:Merienda;font-weight:800;" class="d-none d-sm-inline">UnicodeSearch</span>
				<span id="rowcount" class="px-3">Rows: ${data.length.toLocaleString()}</span>
				<input id="showhidecolumns" type="checkbox" class="ms-2 me-1" ${
					detail ? "checked" : ""
				} title="Toggle detail columns" /> <span class="d-none d-md-inline">Show detail columns</span><span class="d-md-none">Details</span>
				<span class="d-none"><input id="imagecheckbox" type="checkbox" class="ms-2 me-1" ${
					useImages ? "checked" : ""
				} title="Font vs Images" /> <span class="d-none d-md-inline">Use images in examples</span><span class="d-md-none">Images</span></span>
				<a class="d-none d-lg-block float-end" href="https://github.com/FileFormatInfo/unicodesearch">Source</a>
			</span>`,
	});

	table.on("dataFiltered", function (filters, rows) {
		var el = document.getElementById("rowcount");
		var qs = new URLSearchParams(window.location.search);
		for (const col of table.getColumns()) {
			qs.delete(col.getField());
		}
		if (filters && filters.length > 0) {
			el!.innerHTML = `Rows: ${rows.length.toLocaleString()} of ${data.length.toLocaleString()}`;
			for (const f of filters) {
				qs.set(f.field, f.value as string);
			}
		} else {
			el!.innerHTML = `Rows: ${data.length.toLocaleString()}`;
		}
		window.history.replaceState(null, "", "?" + qs);
	});

	table.on("dataSorted", function (sorters, rows) {
		var qs = new URLSearchParams(window.location.search);
		qs.set("sort", sorters[0]?.column.getField());
		qs.set("dir", sorters[0]?.dir);
		window.history.replaceState(null, "", "?" + qs);
	});

	table.on("tableBuilt", function () {
		document.getElementById("showhidecolumns")!.onclick = () => {
			detail = !detail;
			toggleColumns(table, ["age", "block", "category", "entity", "script", "utf8", "utf16"]);
			const qs = new URLSearchParams(window.location.search);
			if (detail) {
				qs.set("detail", "1");
			} else {
				qs.delete("detail");
			}
			window.history.replaceState(null, "", "?" + qs.toString());
		}
		document.getElementById("imagecheckbox")!.onclick = () => {
			useImages = !useImages;
			const qs = new URLSearchParams(window.location.search);
			if (useImages) {
				qs.set("images", "1");
			} else {
				qs.delete("images");
			}
			window.history.replaceState(null, "", "?" + qs.toString());
			table.redraw(true);
		};
	});

	document.getElementById("loading")!.classList.add("d-none");
	document.getElementById("unicodetable")!.classList.remove("d-none");
}

main();
